import { type ReactNode, useState } from "react";

import purify from "dompurify";

import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

export interface ISimpleWarningModalHook {
    openModal: () => void;
    modal: ReactNode;
}

interface ISimpleWarningModalProps {
    title: string;
    tooltip?: string;
    text: string;
    ctaText: string;
    resetState?: () => void;
    onCancel?: () => void;
    onSubmit: () => void;
}

export const useSimpleWarningModal = ({
    title,
    tooltip,
    text,
    ctaText,
    resetState,
    onCancel,
    onSubmit,
}: ISimpleWarningModalProps): ISimpleWarningModalHook => {
    const [isOpen, setOpen] = useState<boolean>(false);

    const openModal = (): void => {
        setOpen(true);
    };

    const closeModal = (): void => {
        resetState?.();
        setOpen(false);
    };

    const cancel = (): void => {
        onCancel?.();
        closeModal();
    };

    const submit = (): void => {
        onSubmit();
        closeModal();
    };

    return {
        openModal,
        modal: (
            <Modal
                isOpen={isOpen}
                onRequestClose={closeModal}
                shouldCloseOnEsc
                shouldCloseOnOverlayClick
                customStyles={{
                    content: {
                        width: "400px",
                    },
                }}
            >
                <Modal.Header>
                    <Modal.Title tooltip={tooltip}>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Content>
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon[400]}
                        padding="16px 0 8px"
                        dangerouslySetInnerHTML={{
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            __html: purify.sanitize(text),
                        }}
                    />
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        type="flat"
                        onClick={cancel}
                    >
                        {translate("global.cancel")}
                    </Button>
                    <Button
                        type="warning"
                        onClick={submit}
                    >
                        {ctaText}
                    </Button>
                </Modal.Footer>
            </Modal>
        ),
    };
};
